import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAsyncData } from "../hooks/useAsyncData";
import { blogService } from "../services/blog.service";
import { IBlogView } from "../interfaces/blog/iBlogView.interface";
import { PageContainer } from "../components/PageContainer";
import { useApi } from "../api/useApi";
import { Seo } from "../components/Seo";
import { infoCardClasses, pageLeadClasses, subtleEyebrowClasses } from "../styles/pageClasses";
import { primaryBtnClasses } from "../styles/uiClasses";
import { blogPostPageClasses, jobDetailsPageClasses } from "../styles/pageSpecificClasses";

export default function BlogPost() {
  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const { request } = useApi();
  const isPreview = Boolean(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id, slug]);

  const { data: post, loading, error } = useAsyncData<IBlogView>(
    () => {
      if (id) {
        return blogService.getPreview(Number(id), request);
      }

      return blogService.getBySlug(slug ?? "");
    },
    [id, request, slug]
  );

  if (loading) {
    return <div className={jobDetailsPageClasses.loading}>Loading article...</div>;
  }

  if (error || !post) {
    return (
      <div className={jobDetailsPageClasses.errorWrap}>
        <p className={jobDetailsPageClasses.errorCopy}>{error ?? "Article not found."}</p>
        <Link to={isPreview ? "/admin/blog" : "/blog"} className={jobDetailsPageClasses.backLink}>
          {isPreview ? "Back to Manage Blog" : "Back to Blog"}
        </Link>
      </div>
    );
  }

  const coverImage = post.images.find((image) => image.isCover) ?? null;
  const galleryImages = post.images.filter((image) => !image.isCover);

  return (
    <PageContainer maxWidth="5xl" contentClassName={blogPostPageClasses.shellSpacing}>
      <Seo
        title={post.seoTitle?.trim() || post.title}
        description={post.seoDescription?.trim() || post.summary || "Read the latest guide from Summer Jobs in Greece."}
        path={isPreview ? undefined : `/blog/${post.slug}`}
        canonicalUrl={isPreview ? undefined : `https://www.summerjobsingreeceguide.com/blog/${post.slug}`}
        image={coverImage?.imageUrl ?? undefined}
        type="article"
        robots={isPreview ? "noindex,nofollow" : "index,follow"}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={isPreview ? "/admin/blog" : "/blog"}
          className={jobDetailsPageClasses.backButton}
        >
          {isPreview ? "Back to Manage Blog" : "Back to Blog"}
        </Link>
        {isPreview && id && (
          <Link to={`/admin/blog/edit/${id}`} className={primaryBtnClasses}>
            Edit Post
          </Link>
        )}
      </div>

      <section className={infoCardClasses}>
        <div className={blogPostPageClasses.headerWrap}>
          <div>
            <span className={subtleEyebrowClasses}>
              {post.categoryName ?? "Blog Post"}
            </span>
            <h1 className={blogPostPageClasses.title}>{post.title}</h1>
            {post.summary && <p className={`${blogPostPageClasses.summary} ${pageLeadClasses}`}>{post.summary}</p>}
          </div>

          <div className={blogPostPageClasses.meta}>
            <span>{formatDate(post.publishedAt ?? post.createdDate)}</span>
            <span>{post.readTimeMinutes ? `${post.readTimeMinutes} min read` : "Article"}</span>
          </div>
        </div>

        {coverImage && (
          <div className="space-y-3">
            <img
              src={coverImage.imageUrl}
              alt={coverImage.altText ?? post.title}
              className={blogPostPageClasses.coverImage}
            />
            <ImageCredit
              photographerName={coverImage.photographerName}
              photographerUrl={coverImage.photographerUrl}
              sourceName={coverImage.sourceName}
              sourceUrl={coverImage.sourceUrl}
              locationName={coverImage.locationName}
            />
          </div>
        )}
      </section>

      <section className={infoCardClasses}>
        <div
          className={blogPostPageClasses.content}
          dangerouslySetInnerHTML={{ __html: post.contentHtml }}
        />
      </section>

      {galleryImages.length > 0 && (
        <section className={infoCardClasses}>
          <h2 className={blogPostPageClasses.galleryTitle}>Article Gallery</h2>
          <div className={blogPostPageClasses.galleryGrid}>
            {galleryImages.map((image) => (
              <figure key={image.id || `${image.sortOrder}-${image.imageUrl}`} className={blogPostPageClasses.galleryItem}>
                <img
                  src={image.imageUrl}
                  alt={image.altText ?? post.title}
                  className={blogPostPageClasses.galleryImage}
                />
                {(image.caption || image.photographerName || image.sourceName) && (
                  <figcaption className={blogPostPageClasses.galleryCaption}>
                    {image.caption && <span>{image.caption}</span>}
                    <ImageCredit
                      photographerName={image.photographerName}
                      photographerUrl={image.photographerUrl}
                      sourceName={image.sourceName}
                      sourceUrl={image.sourceUrl}
                      locationName={image.locationName}
                    />
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}
    </PageContainer>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "Recently added";
  return new Date(value).toLocaleDateString();
}

type ImageCreditProps = {
  photographerName?: string | null;
  photographerUrl?: string | null;
  sourceName?: string | null;
  sourceUrl?: string | null;
  locationName?: string | null;
};

function ImageCredit({ photographerName, photographerUrl, sourceName, sourceUrl, locationName }: ImageCreditProps) {
  if (!photographerName && !sourceName && !locationName) return null;

  return (
    <span className="block text-xs text-[#6a6255]">
      Photo by{" "}
      {photographerUrl ? (
        <a href={photographerUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2">
          {photographerName ?? "Unknown photographer"}
        </a>
      ) : (
        photographerName ?? "Unknown photographer"
      )}
      {sourceName && (
        <>
          {" "}on{" "}
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2">
              {sourceName}
            </a>
          ) : (
            sourceName
          )}
        </>
      )}
      {locationName && (
        <>
          {" "}in <span className="font-medium text-[#4d463c]">{locationName}</span>
        </>
      )}
    </span>
  );
}
